import { useMemo } from "react";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";

import { Color, Panel, Tag } from "capitalrx-components-2.0/lib/general";
import { Grid, Shelf, Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Body, Semibold, Subheading, TextColor } from "capitalrx-components-2.0/lib/typography";
import { getFormattedValue } from "capitalrx-components-2.0/lib/general/Table/helpers";

import { ThemedButton } from "@/App/components";
import { MedicalClaimListObject } from "@/App/api/claims";

import "./ClaimsPage.scss";
import { ClaimInfoStack } from ".";

export interface MedicalClaimListItemProps {
  readonly medical_claim: MedicalClaimListObject;
    readonly onClaimView: (medical_claim: MedicalClaimListObject) => void;
}

export const MedicalClaimListItem = ({ medical_claim, onClaimView }: MedicalClaimListItemProps) => {
  const { t: translate } = useTranslation();
  const { triColumns } = useResponsive({
    triColumns: {
      mobile: 12,
      tablet: 6,
      desktop: 4,
    },
  });
  const {
    id,
    status,
    network,
    received_at,
    minimum_service_date,
    total_patient_pay_amount,
    billing_provider_first_name,
    billing_provider_last_name_or_organizational_name,
    direct_member_reimbursement_indicator,
  } = medical_claim;

  const formattedServiceDate = DateTime.fromISO(minimum_service_date, { zone: "utc" }).toFormat("DDD");
  const formattedReceivedDate = DateTime.fromISO(received_at, { zone: "utc" }).toFormat("DDD");

  const statusTag = useMemo(() => {
    let statusTagElement = <Tag type="success-high-contrast">{translate("CLAIMS.statusOptions.approved")}</Tag>;
    if (status === "denied") {
      statusTagElement = <Tag type="error-high-contrast">{translate("CLAIMS.statusOptions.denied")}</Tag>;
    }
    if (status === "partially_paid") {
      statusTagElement = <Tag type="system-high-contrast">{translate("CLAIMS.statusOptions.partiallyApproved")}</Tag>;
    }
    if (status === "pended") {
      statusTagElement = <Tag type="warning-high-contrast">{translate("CLAIMS.statusOptions.inProgress")}</Tag>;
    }
    return statusTagElement;
  }, [status, translate]);

  const isDirectMemberReimbursement = (direct_member_reimbursement_indicator ?? "0") !== "0";

  return (
    <Panel className="member-portal-panel" appearance="primary">
      <Stack>
        <Grid>
          <Grid.Span columns={8}>
            <Stack spacing="none">
              <Semibold><TextColor appearance={Color.DarkGrey["400"]}>{formattedServiceDate}</TextColor></Semibold>
              <Subheading>{billing_provider_first_name && billing_provider_first_name} {billing_provider_last_name_or_organizational_name}</Subheading>
              <Body>
                {network === "in_network_tier_1" && translate("CLAIMS.networkOptions.inNetwork")}
                {network === "out_of_network" && translate("CLAIMS.networkOptions.outOfNetwork")}
              </Body>
            </Stack>
          </Grid.Span>
          <Grid.Span columns={4}>
            {status !== "pended" && (
              <Stack spacing="none" alignment="right">
                <Body textAlign="right">
                  <Semibold>
                    <TextColor appearance={Color.DarkGrey["400"]}>{translate("CLAIMS.claimListItem.patientResponsibility")}</TextColor>
                  </Semibold>
                </Body>
                <Semibold>${getFormattedValue(total_patient_pay_amount)}</Semibold>
              </Stack>
            )}
          </Grid.Span>
        </Grid>
        <Grid>
          <Grid.Span columns={triColumns}>
            <ClaimInfoStack title="ID" body={String(id).padStart(3, "0")} />
          </Grid.Span>
          <Grid.Span columns={triColumns}>
            <ClaimInfoStack title={translate("CLAIMS.claimListItem.receivedDate")} body={formattedReceivedDate} />
          </Grid.Span>
          <Grid.Span columns={triColumns}>
            <ClaimInfoStack title={translate("CLAIMS.claimListItem.status")} body={statusTag} />
          </Grid.Span>
        </Grid>
        <Shelf alignment="bottom">
          <ThemedButton icon="eye" testId={`view-claim-${id}-button`} onClick={() => onClaimView(medical_claim)} appearance="secondary">
            {translate("CLAIMS.claimListItem.view")}
          </ThemedButton>
          <Shelf.Spacer />
          {
            isDirectMemberReimbursement && (
              <Body textAlign="right">
                <Semibold>
                  <TextColor appearance={Color.DarkGrey["400"]}>{translate("CLAIMS.claimListItem.reimbursementClaim")}</TextColor>
                </Semibold>
              </Body>
            )
          }
        </Shelf>
      </Stack>
    </Panel>
  );
};
